import { useQuery } from "@hooks/useQuery";
import { Tabs } from "antd";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DynamicForm, FormConfig } from "~/Components/DynamicForm/DynamicForm";
import { useNotification } from "~/Hooks/useNotification";
import { useDocumentDetails } from "~/Modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { post, put } from "~/Services";
import { eramanHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { fields } from "../constants";
import { useAboutUs, useContactUs, useWelcomeNote } from "../Pages.hook";
import Awards from "./Awards/Awards";
import { Careers } from "./Careers/Careers";
import ContactUs from "./ContactUs/ContactUs";
import { News } from "./News/News";

export const AboutEraman = () => {
  const query = useQuery();
  const { openToast } = useNotification();
  const navigate = useNavigate();
  const { data: aboutUsData, isLoading: isLoadingAboutUs, refetch: refetchAboutUs } = useAboutUs();
  const { data: contactUsData, isLoading: isLoadingContactUs, refetch: refetchContactUs } = useContactUs();

  const { data: welcomeNoteData, isLoading: isLoadingWelcomeNote, refetch: refetchWelcomeNote } = useWelcomeNote();
  const {
    data: pagesDataById,
    isLoading: isLoadingPagesById,
    refetch: refetchData,
  } = useDocumentDetails(query?.get("id") || "");

  useEffect(() => {
    if (query?.get("id")) {
      return;
    }

    const subActiveTab = query.get("subActiveTab");
    if (query.get("activeTab") === "aboutEraman") {
      navigate(`/dedicated/eraman/pages?activeTab=aboutEraman&subActiveTab=${subActiveTab ?? "aboutUs"}`, {
        replace: true,
      });

      if (subActiveTab === "aboutUs") {
        refetchAboutUs();
      } else if (subActiveTab === "contactUs") {
        refetchContactUs();
      } else if (subActiveTab === "welcomeNote") {
        refetchWelcomeNote();
      } else {
      }
      // refetchData();
    }
  }, [query.get("activeTab"), query.get("subActiveTab")]);

  useEffect(() => {
    if (query?.get("id")) {
      refetchData();
    }
  }, [query?.get("id")]);

  const AboutUsFormConfig: FormConfig = {
    initialValues: {
      // Title
      title: pagesDataById?.payload ? decodeHTML(pagesDataById?.payload?.title) : decodeHTML(aboutUsData?.[0]?.title),
      titleMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.titleMs)
        : decodeHTML(aboutUsData?.[0]?.titleMs),
      titleZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.titleZh)
        : decodeHTML(aboutUsData?.[0]?.titleZh),

      // Description
      field_content_description_en: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_content_description_en)
        : decodeHTML(aboutUsData?.[0]?.field_content_description_en),
      field_content_description_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_content_description_ms)
        : decodeHTML(aboutUsData?.[0]?.field_content_description_ms),
      field_content_description_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_content_description_zh)
        : decodeHTML(aboutUsData?.[0]?.field_content_description_zh),

      // Vision
      visionIcon: pagesDataById?.payload
        ? [
            {
              uid: "-1",
              name: "visionIcon.png",
              status: "done",
              url: pagesDataById?.payload?.visionIcon,
            },
          ]
        : aboutUsData?.[0]?.visionIcon
          ? [
              {
                uid: "-1",
                name: "visionIcon.png",
                status: "done",
                url: aboutUsData?.[0]?.visionIcon,
              },
            ]
          : "",
      visionTitle: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.visionTitle)
        : decodeHTML(aboutUsData?.[0]?.visionTitle),
      visionTitleMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.visionTitleMs)
        : decodeHTML(aboutUsData?.[0]?.visionTitleMs),
      visionTitleZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.visionTitleZh)
        : decodeHTML(aboutUsData?.[0]?.visionTitleZh),
      visionDesc: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.visionDesc)
        : decodeHTML(aboutUsData?.[0]?.visionDesc),
      visionDescMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.visionDescMs)
        : decodeHTML(aboutUsData?.[0]?.visionDescMs),
      visionDescZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.visionDescZh)
        : decodeHTML(aboutUsData?.[0]?.visionDescZh),

      // Mission
      missionIcon: pagesDataById?.payload
        ? [
            {
              uid: "-1",
              name: "missionIcon.png",
              status: "done",
              url: pagesDataById?.payload?.missionIcon,
            },
          ]
        : aboutUsData?.[0]?.missionIcon
          ? [
              {
                uid: "-1",
                name: "missionIcon.png",
                status: "done",
                url: aboutUsData?.[0]?.missionIcon,
              },
            ]
          : "",

      missionTitle: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.missionTitle)
        : decodeHTML(aboutUsData?.[0]?.missionTitle),
      missionTitleMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.missionTitleMs)
        : decodeHTML(aboutUsData?.[0]?.missionTitleMs),
      missionTitleZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.missionTitleZh)
        : decodeHTML(aboutUsData?.[0]?.missionTitleZh),
      missionDesc: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.missionDesc)
        : decodeHTML(aboutUsData?.[0]?.missionDesc),
      missionDescMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.missionDescMs)
        : decodeHTML(aboutUsData?.[0]?.missionDescMs),
      missionDescZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.missionDescZh)
        : decodeHTML(aboutUsData?.[0]?.missionDescZh),

      // Core Values
      coreValuesIcon: pagesDataById?.payload
        ? [
            {
              uid: "-1",
              name: "coreValuesIcon.png",
              status: "done",
              url: pagesDataById?.payload?.coreValuesIcon,
            },
          ]
        : aboutUsData?.[0]?.coreValuesIcon
          ? [
              {
                uid: "-1",
                name: "coreValuesIcon.png",
                status: "done",
                url: aboutUsData?.[0]?.coreValuesIcon,
              },
            ]
          : "",
      coreValuesTitle: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.coreValuesTitle)
        : decodeHTML(aboutUsData?.[0]?.coreValuesTitle),
      coreValuesTitleMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.coreValuesTitleMs)
        : decodeHTML(aboutUsData?.[0]?.coreValuesTitleMs),
      coreValuesTitleZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.coreValuesTitleZh)
        : decodeHTML(aboutUsData?.[0]?.coreValuesTitleZh),
      coreValuesDesc: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.coreValuesDesc)
        : decodeHTML(aboutUsData?.[0]?.coreValuesDesc),
      coreValuesDescMs: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.coreValuesDescMs)
        : decodeHTML(aboutUsData?.[0]?.coreValuesDescMs),
      coreValuesDescZh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.coreValuesDescZh)
        : decodeHTML(aboutUsData?.[0]?.coreValuesDescZh),

      // Video
      videoUrl: pagesDataById?.payload
        ? decodeHTML([
            {
              uid: "-1",
              name: "video_url.mp4",
              status: "done",
              url: pagesDataById?.payload?.videoUrl,
            },
          ])
        : aboutUsData?.[0]?.videoUrl
          ? decodeHTML([
              {
                uid: "-1",
                name: "video_url.mp4",
                status: "done",
                url: aboutUsData?.[0]?.videoUrl,
              },
            ])
          : "",
    },
    fields: fields.aboutUs,
  };

  const WelcomeNoteFormConfig: FormConfig = {
    initialValues: {
      field_page_title_en: pagesDataById
        ? decodeHTML(pagesDataById.payload.field_page_title_en || "")
        : welcomeNoteData
          ? decodeHTML(welcomeNoteData?.[0]?.field_page_title_en || "")
          : "",
      field_page_title_ms: pagesDataById
        ? decodeHTML(pagesDataById.payload.field_page_title_ms || "")
        : welcomeNoteData
          ? decodeHTML(welcomeNoteData?.[0]?.field_page_title_ms || "")
          : "",
      field_page_title_zh: pagesDataById
        ? decodeHTML(pagesDataById.payload.field_page_title_zh || "")
        : welcomeNoteData
          ? decodeHTML(welcomeNoteData?.[0]?.field_page_title_zh || "")
          : "",

      field_content_description_en: pagesDataById
        ? decodeHTML(pagesDataById.payload.field_content_description_en || "")
        : welcomeNoteData
          ? decodeHTML(welcomeNoteData?.[0]?.field_content_description_en || "")
          : "",

      field_content_description_ms: pagesDataById
        ? decodeHTML(pagesDataById.payload.field_content_description_ms || "")
        : welcomeNoteData
          ? decodeHTML(welcomeNoteData?.[0]?.field_content_description_ms || "")
          : "",

      field_content_description_zh: pagesDataById
        ? decodeHTML(pagesDataById.payload.field_content_description_zh || "")
        : welcomeNoteData
          ? decodeHTML(welcomeNoteData?.[0]?.field_content_description_zh || "")
          : "",
    },
    fields: fields.welcomeNote,
  };

  const ContactUsFormConfig: FormConfig = {
    initialValues: {
      title: pagesDataById?.payload ? decodeHTML(pagesDataById?.payload?.title) : decodeHTML(contactUsData?.[0]?.title),
      field_page_title_ms: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_page_title_ms)
        : decodeHTML(contactUsData?.[0]?.field_page_title_ms),
      field_page_title_zh: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_page_title_zh)
        : decodeHTML(contactUsData?.[0]?.field_page_title_zh),
      body: pagesDataById?.payload ? decodeHTML(pagesDataById?.payload?.body) : decodeHTML(contactUsData?.[0]?.body),
      field_common_malay_description: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_common_malay_description)
        : decodeHTML(contactUsData?.[0]?.field_common_malay_description),
      field_common_mandarin_descriptio: pagesDataById?.payload
        ? decodeHTML(pagesDataById?.payload?.field_common_mandarin_descriptio)
        : decodeHTML(contactUsData?.[0]?.field_common_mandarin_descriptio),
    },
    fields: fields.contactUs,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["bannerImage", "missionIcon", "visionIcon", "coreValuesIcon"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "ERAMAN");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          data[key] = field[0].url;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    if (data.videoUrl && data.videoUrl.length > 0) {
      const file = data.videoUrl[0];
      if (file.originFileObj) {
        // new upload
        const formData = new FormData();
        formData.append("upload-file", file.originFileObj);
        formData.append("application", "ERAMAN");
        const res: any = await post("management/upload-file", formData);

        if (res) {
          data.videoUrl = res.response.data;
        }
      } else {
        // existing file
        data.videoUrl = file.url || "";
      }
    } else {
      data.videoUrl = "";
    }

    const payload = {
      action: action,
      method: method,
      module: "Pages",
      subModule: `${query.get("activeTab")}_${query.get("subActiveTab")}`,
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: {
        ...(action === "UPDATE" && { id: aboutUsData?.[0]?.id || pagesDataById?.payload?.id }),
        ...data,
        published: true,
      },
      status: status,
    };
    console.log(payload, "payload");
    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
          // refetchDocument();
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  const createAboutEramanSubItems = (label: string, key: string, formConfig: FormConfig, apiUrl: any) => ({
    label,
    key,
    children: (
      <DynamicForm
        config={formConfig}
        isLoading={isLoadingAboutUs || isLoadingContactUs || isLoadingPagesById || isLoadingWelcomeNote}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(data, apiUrl, "UPDATE", "PATCH", buttonValue === "save" ? "DRAFT" : "PENDING", reviewNotes)
        }
      />
    ),
  });

  const aboutEramanChildItems = [
    createAboutEramanSubItems(
      "About Us",
      "aboutUs",
      AboutUsFormConfig,
      `${eramanHost}/api/about-us-page-data-crud-api/${aboutUsData?.[0]?.id || pagesDataById?.payload?.id}`
    ),

    createAboutEramanSubItems(
      "Welcome Note",
      "welcomeNote",
      WelcomeNoteFormConfig,
      `${eramanHost}/api/page-headers-data-list-crud-api/${welcomeNoteData?.[0]?.id || pagesDataById?.payload?.id}`
    ),
    {
      label: "Awards",
      key: "awards",
      children: <Awards />,
    },
    {
      label: "News",
      key: "news",
      children: <News />,
    },
    {
      label: "Careers",
      key: "careers",
      children: <Careers />,
    },
    // createAboutEramanSubItems(
    //   "Contact Us",
    //   "contactUs",
    //   ContactUsFormConfig,
    //   `${eramanHost}/api/contact-center-page-data-update-api/${pagesDataById?.payload?.id || contactUsData?.[0]?.id}`
    // ),
    {
      label: "Contact Us",
      key: "contactUs",
      children: <ContactUs />,
    },
    // createAboutEramanSubItems(
    //   "Homepage Popup",
    //   "homePagePopup",
    //   HomePagePopupFormConfig
    // ),
  ];

  return (
    // <ContentWrapper title={"About Eraman"}>
    <Tabs
      activeKey={query?.get("subActiveTab") || ""}
      items={aboutEramanChildItems}
      onChange={(activeKey) => {
        console.log(activeKey, "activeKey");
        let url = `${window.location.pathname}?activeTab=${query.get("activeTab")}&subActiveTab=${activeKey}`;

        if (activeKey === "awards") {
          url = `${window.location.pathname}?activeTab=${query.get("activeTab")}&subActiveTab=awards&thirdChildActiveTab=awardsContent`;
        }

        if (activeKey === "contactUs") {
          url = `${window.location.pathname}?activeTab=${query.get("activeTab")}&subActiveTab=contactUs&thirdChildActiveTab=contactDetails`;
        }

        navigate(url);
      }}
    />

    // </ContentWrapper>
  );
};
